/**
 * Вкладки «Склади» і «Каси» для картки організації (2026-06-29).
 * Перенесено з колишньої окремої сторінки RefsPage — тепер усе керування
 * об'єктами організації (склади/каси/банк-рахунки) лише через її картку.
 *
 * orgId — id ВІДКРИТОЇ організації (не dropdown). canManage — право
 * refs.warehouses.edit у цій org (паттерн view=wide, edit=narrow).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  refs,
  type Warehouse,
  type CashRegister,
  type WarehouseCreate,
} from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import ProjectSelect from '@/components/ProjectSelect';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

// ============== Склади ==============

interface WhEditing {
  id?: string;
  organization_id: string;
  code: string | null;
  name: string;
  warehouse_type: 'WHOLESALE' | 'RETAIL' | 'NTT';
  address: string;
  is_storage: boolean;
  default_direction_id: string | null;
}

export function OrgWarehousesTab({ orgId, canManage, cantReason }: {
  orgId: string; canManage: boolean; cantReason: string;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const list = useQuery({
    queryKey: ['org-warehouses', orgId],
    queryFn: () => refs.warehouses(orgId),
  });
  const [editing, setEditing] = useState<WhEditing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const e = editing!;
      const payload: WarehouseCreate = {
        organization_id: e.organization_id,
        code: e.code,
        name: e.name,
        warehouse_type: e.warehouse_type,
        address: e.address || null,
        is_storage: e.is_storage,
        default_direction_id: e.default_direction_id,
      };
      return e.id
        ? refs.updateWarehouse(e.id, {
            code: payload.code, name: payload.name,
            warehouse_type: payload.warehouse_type,
            address: payload.address, is_storage: payload.is_storage,
            default_direction_id: payload.default_direction_id,
          })
        : refs.createWarehouse(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-warehouses'] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.removeWarehouse(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-warehouses'] }),
  });

  const cols = useMemo<ColumnDef<Warehouse>[]>(() => [
    {
      key: 'code', header: 'Код', width: 130, importBadge: true,
      sortValue: (w) => w.code ?? '',
      cell: (w) => <code>{w.code}</code>,
    },
    {
      key: 'name', header: 'Назва', width: 250,
      sortValue: (w) => w.name, cell: (w) => w.name,
    },
    {
      key: 'warehouse_type', header: 'Тип', width: 130,
      sortValue: (w) => w.warehouse_type, cell: (w) => w.warehouse_type,
    },
    {
      key: 'direction', header: 'Напрямок', width: 110, align: 'center',
      sortValue: (w) => (w.default_direction_id ? 1 : 0),
      cell: (w) => (w.default_direction_id ? '✓' : '—'),
    },
    {
      key: 'actions', header: '', width: 90, align: 'right',
      cell: (w) => (
        <>
          <button
            disabled={!canManage}
            title={canManage ? undefined : cantReason}
            onClick={() => setEditing({
              id: w.id, organization_id: w.organization_id,
              code: w.code, name: w.name,
              warehouse_type: w.warehouse_type as WhEditing['warehouse_type'],
              address: w.address ?? '',
              is_storage: w.is_storage ?? true,
              default_direction_id: w.default_direction_id ?? null,
            })}>✎</button>
          <button className="danger" style={{ marginLeft: '0.3rem' }}
                  disabled={!canManage}
                  title={canManage ? undefined : cantReason}
                  onClick={() => confirm('Видалити?').then((ok) => { if (ok) remove.mutate(w.id); })}>
            ×
          </button>
        </>
      ),
    },
  ], [canManage, cantReason, confirm, remove]);

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <button
          className="primary"
          disabled={!canManage}
          title={canManage ? undefined : cantReason}
          onClick={() => setEditing({
            organization_id: orgId, code: null, name: '',
            warehouse_type: 'WHOLESALE', address: '',
            is_storage: true, default_direction_id: null,
          })}
        >
          + Новий склад
        </button>
      </div>

      {editing && (
        <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 150px', gap: '0.5rem' }}>
            <input
              placeholder="Код (auto: WH-N)"
              title="Залиште порожнім — система присвоїть автоматично"
              value={editing.code ?? ''}
              onChange={(e) => setEditing({ ...editing, code: e.target.value || null })}
            />
            <input placeholder="Назва" value={editing.name}
                   onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <select value={editing.warehouse_type}
                    onChange={(e) => setEditing({ ...editing, warehouse_type: e.target.value as WhEditing['warehouse_type'] })}>
              <option value="WHOLESALE">Оптовий</option>
              <option value="RETAIL">Роздрібний</option>
              <option value="NTT">НТТ</option>
            </select>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <input style={{ width: '100%' }} placeholder="Адреса (опц.)"
                   value={editing.address}
                   onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.85rem' }}>
              <input type="checkbox" checked={editing.is_storage}
                     onChange={(e) => setEditing({ ...editing, is_storage: e.target.checked })} />
              {' '}Склад зберігання
            </label>
            <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Напрямок (за замовч.):</label>
            <ProjectSelect
              value={editing.default_direction_id}
              onChange={(v) => setEditing({ ...editing, default_direction_id: v })}
              organizationId={editing.organization_id}
              kind="DIRECTION"
            />
            <button className="primary" onClick={() => save.mutate()}>Зберегти</button>
            <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      )}

      <DataTable<Warehouse>
        rows={list.data ?? []}
        columns={cols}
        rowKey={(w) => w.id}
        loading={list.isLoading}
        emptyText="Немає складів."
        initialSortKey="code"
      />
    </div>
  );
}

// ============== Каси ==============

interface CrEditing {
  id?: string;
  organization_id: string;
  code: string | null;
  name: string;
  currency_id: string;
  default_direction_id: string | null;
}

export function OrgCashRegistersTab({ orgId, canManage, cantReason }: {
  orgId: string; canManage: boolean; cantReason: string;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const list = useQuery({
    queryKey: ['org-cash-registers', orgId],
    queryFn: () => refs.cashRegisters(orgId),
  });
  // Валюти з API (НЕ хардкод id — id різний по оточеннях/інсталяціях).
  const currencies = useQuery({
    queryKey: ['currencies'],
    queryFn: () => refs.currencies(),
  });
  const [editing, setEditing] = useState<CrEditing | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const e = editing!;
      return e.id
        ? refs.updateCashRegister(e.id, {
            code: e.code, name: e.name, currency_id: e.currency_id,
            default_direction_id: e.default_direction_id,
          })
        : refs.createCashRegister({
            organization_id: e.organization_id,
            code: e.code, name: e.name, currency_id: e.currency_id,
            default_direction_id: e.default_direction_id,
          });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-cash-registers'] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.removeCashRegister(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-cash-registers'] }),
  });

  const UAH_ID = currencies.data?.find((c) => c.code === 'UAH')?.id
    ?? currencies.data?.[0]?.id ?? '';

  const cols = useMemo<ColumnDef<CashRegister>[]>(() => [
    {
      key: 'code', header: 'Код', width: 130, importBadge: true,
      sortValue: (c) => c.code ?? '',
      cell: (c) => <code>{c.code}</code>,
    },
    {
      key: 'name', header: 'Назва', width: 250,
      sortValue: (c) => c.name, cell: (c) => c.name,
    },
    {
      key: 'direction', header: 'Напрямок', width: 110, align: 'center',
      sortValue: (c) => (c.default_direction_id ? 1 : 0),
      cell: (c) => (c.default_direction_id ? '✓' : '—'),
    },
    {
      key: 'actions', header: '', width: 90, align: 'right',
      cell: (c) => (
        <>
          <button
            disabled={!canManage}
            title={canManage ? undefined : cantReason}
            onClick={() => setEditing({
              id: c.id, organization_id: c.organization_id,
              code: c.code, name: c.name,
              currency_id: c.currency_id,
              default_direction_id: c.default_direction_id ?? null,
            })}>✎</button>
          <button className="danger" style={{ marginLeft: '0.3rem' }}
                  disabled={!canManage}
                  title={canManage ? undefined : cantReason}
                  onClick={() => confirm('Видалити?').then((ok) => { if (ok) remove.mutate(c.id); })}>
            ×
          </button>
        </>
      ),
    },
  ], [canManage, cantReason, confirm, remove]);

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <button
          className="primary"
          disabled={!canManage || !UAH_ID}
          title={canManage ? (UAH_ID ? undefined : 'Завантаження валют…') : cantReason}
          onClick={() => setEditing({
            organization_id: orgId, code: null, name: '',
            currency_id: UAH_ID, default_direction_id: null,
          })}
        >
          + Нова каса
        </button>
      </div>

      {editing && (
        <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem' }}>
            <input
              placeholder="Код (auto: CASH-N)"
              title="Залиште порожнім — система присвоїть автоматично"
              value={editing.code ?? ''}
              onChange={(e) => setEditing({ ...editing, code: e.target.value || null })}
            />
            <input placeholder="Назва" value={editing.name}
                   onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Напрямок (за замовч.):</label>
            <ProjectSelect
              value={editing.default_direction_id}
              onChange={(v) => setEditing({ ...editing, default_direction_id: v })}
              organizationId={editing.organization_id}
              kind="DIRECTION"
            />
            <button className="primary" onClick={() => save.mutate()}>Зберегти</button>
            <button onClick={() => { setEditing(null); setError(null); }}>Скасувати</button>
          </div>
          {error && <div className="error">{error}</div>}
        </div>
      )}

      <DataTable<CashRegister>
        rows={list.data ?? []}
        columns={cols}
        rowKey={(c) => c.id}
        loading={list.isLoading}
        emptyText="Немає кас."
        initialSortKey="code"
      />
    </div>
  );
}
